import React, { useState, useEffect } from 'react';
import { 
  Globe, Plus, Edit2, Trash2, Search, 
  CheckCircle, Loader2, Flag, Users
} from 'lucide-react';
import { theme } from '../../styles/theme';
import administrationApi, {
  Country,
  CountryCreateRequest,
  CountryUpdateRequest,
} from '../../api/administrationApi';

interface AdminCountriesProps {
  darkMode: boolean;
}

const AdminCountries: React.FC<AdminCountriesProps> = ({ darkMode }) => {
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCountryModal, setShowCountryModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [countryFormData, setCountryFormData] = useState({
    iso_code: '',
    name: '',
    brave_country_code: '',
    region: '',
    currency_code: '',
    flag_emoji: '',
    display_order: 999,
    is_active: true,
  });

  const [countries, setCountries] = useState<Country[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 24;

  // Fetch countries from database
  useEffect(() => {
    fetchCountries();
  }, []);

  const fetchCountries = async () => {
    try {
      console.log('Fetching countries...');
      setLoading(true);
      // Fetch all countries by setting a high limit
      // This ensures search works across all countries, not just first page
      const response = await administrationApi.getCountries({
        limit: 1000, // Fetch all countries at once
        page: 1
      });
      console.log('Countries response:', response);
      setCountries(response.countries);
      console.log('Countries loaded:', response.countries.length);
    } catch (error) {
      console.error('Failed to fetch countries:', error);
      // Show error details
      if (error instanceof Error) {
        console.error('Error message:', error.message);
        console.error('Error stack:', error.stack);
      }
    } finally {
      setLoading(false);
    }
  };


  // Country management functions
  const handleAddCountry = () => {
    setSelectedCountry(null);
    setCountryFormData({
      iso_code: '',
      name: '',
      brave_country_code: '',
      region: '',
      currency_code: '',
      flag_emoji: '',
      display_order: countries.length + 1,
      is_active: true,
    });
    setShowCountryModal(true);
  };

  const handleEditCountry = (country: Country) => {
    setSelectedCountry(country);
    setCountryFormData({
      iso_code: country.iso_code,
      name: country.name,
      brave_country_code: country.brave_country_code,
      region: country.region,
      currency_code: country.currency_code,
      flag_emoji: country.flag_emoji,
      display_order: country.display_order,
      is_active: country.is_active,
    });
    setShowCountryModal(true);
  };

  const handleDeleteCountry = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this country?')) {
      try {
        await administrationApi.deleteCountry(id);
        await fetchCountries();
      } catch (err) {
        console.error('Failed to delete country:', err);
      }
    }
  };

  const handleSaveCountry = async () => {
    setSaving(true);

    try {
      if (selectedCountry) {
        // Update existing country
        const updateData: CountryUpdateRequest = {
          name: countryFormData.name,
          brave_country_code: countryFormData.brave_country_code,
          region: countryFormData.region,
          currency_code: countryFormData.currency_code,
          flag_emoji: countryFormData.flag_emoji,
          display_order: countryFormData.display_order,
          is_active: countryFormData.is_active,
        };
        await administrationApi.updateCountry(selectedCountry.id, updateData);
        await fetchCountries();
      } else {
        // Create new country
        const createData: CountryCreateRequest = {
          iso_code: countryFormData.iso_code,
          name: countryFormData.name,
          brave_country_code: countryFormData.brave_country_code,
          region: countryFormData.region,
          currency_code: countryFormData.currency_code,
          flag_emoji: countryFormData.flag_emoji,
          display_order: countryFormData.display_order,
          is_active: countryFormData.is_active,
        };
        await administrationApi.createCountry(createData);
        await fetchCountries();
      }
      setShowCountryModal(false);
    } catch (err) {
      console.error('Failed to save country:', err);
      // For now, just add to local state as a mock
      if (!selectedCountry) {
        const newCountry: Country = {
          id: Date.now(),
          ...countryFormData,
        };
        setCountries([...countries, newCountry]);
      } else {
        setCountries(countries.map(c => 
          c.id === selectedCountry.id 
            ? { ...selectedCountry, ...countryFormData }
            : c
        ));
      }
      setShowCountryModal(false);
    } finally {
      setSaving(false);
    }
  };


  // Filter countries based on search query
  const filteredCountries = countries.filter(country =>
    country.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    country.iso_code.toLowerCase().includes(searchQuery.toLowerCase()) ||
    country.brave_country_code.toLowerCase().includes(searchQuery.toLowerCase()) ||
    country.region.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Reset to first page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  // Calculate pagination
  const totalPages = Math.ceil(filteredCountries.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedCountries = filteredCountries.slice(startIndex, endIndex);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2" style={{ 
            color: darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary 
          }}>
            <Globe className="w-6 h-6" style={{ color: theme.colors.primary.main }} />
            Country Management
          </h1>
          <p className="text-sm mt-1" style={{ color: theme.colors.text.secondary }}>
            Manage country configurations for various services
          </p>
        </div>
      </div>


      {/* Tab Content */}
      <div className="rounded-xl" style={{
        backgroundColor: darkMode ? theme.colors.background.darkPaper : theme.colors.background.paper,
        boxShadow: theme.widget.boxShadow,
      }}>
        {(
          <div className="p-6">
            {/* Countries Search and Actions */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-4 flex-1">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4" style={{ 
                    color: theme.colors.text.secondary 
                  }} />
                  <input
                    type="text"
                    placeholder="Search countries..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 rounded-lg focus:outline-none"
                    style={{
                      backgroundColor: darkMode ? theme.colors.background.dark : theme.colors.background.default,
                      border: `1px solid ${darkMode ? theme.colors.border.dark : theme.colors.border.light}`,
                      color: darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary,
                    }}
                  />
                </div>
                <select className="px-4 py-2 rounded-lg" style={{
                  backgroundColor: darkMode ? theme.colors.background.dark : theme.colors.background.default,
                  border: `1px solid ${darkMode ? theme.colors.border.dark : theme.colors.border.light}`,
                  color: darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary,
                }}>
                  <option>All Regions</option>
                  <option>North America</option>
                  <option>Europe</option>
                  <option>Asia Pacific</option>
                  <option>Africa</option>
                  <option>South America</option>
                </select>
              </div>
              <button 
                onClick={handleAddCountry}
                className="px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 text-white" 
                style={{
                  backgroundColor: theme.colors.primary.main,
                }}
              >
                <Plus className="w-4 h-4" />
                <span>Add Country</span>
              </button>
            </div>

            {/* Countries Grid */}
            {loading ? (
              <div className="flex justify-center items-center py-12">
                <Loader2 className="w-8 h-8 animate-spin" style={{ color: theme.colors.primary.main }} />
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                {paginatedCountries.map((country) => (
                  <div 
                    key={country.id}
                    className="relative rounded-lg p-4 transition-all hover:shadow-lg"
                    style={{
                      backgroundColor: darkMode ? theme.colors.background.darkPaper : '#FAFAFA',
                      border: `1px solid ${darkMode ? theme.colors.border.dark : theme.colors.border.light}`,
                    }}
                  >
                    {/* Active/Inactive indicator */}
                    <div className="absolute top-2 right-2">
                      <div 
                        className="w-2 h-2 rounded-full"
                        style={{
                          backgroundColor: country.is_active ? theme.colors.status.success : theme.colors.status.error,
                        }}
                        title={country.is_active ? 'Active' : 'Inactive'}
                      />
                    </div>
                    
                    {/* Country Info */}
                    <div className="flex flex-col items-center text-center">
                      <div className="text-4xl mb-2">
                        {country.flag_emoji || '🏳️'}
                      </div>
                      <div className="font-medium mb-1" style={{ 
                        color: darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary 
                      }}>
                        {country.name}
                      </div>
                      <div className="text-xs" style={{ color: theme.colors.text.secondary }}>
                        {country.iso_code} / {country.brave_country_code}
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex justify-center space-x-2 mt-3">
                      <button 
                        onClick={() => handleEditCountry(country)}
                        className="p-1.5 rounded hover:bg-black hover:bg-opacity-5 transition-colors"
                        title="Edit"
                      >
                        <Edit2 className="w-3.5 h-3.5" style={{ color: theme.colors.text.secondary }} />
                      </button>
                      <button 
                        onClick={() => handleDeleteCountry(country.id)}
                        className="p-1.5 rounded hover:bg-black hover:bg-opacity-5 transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-3.5 h-3.5" style={{ color: theme.colors.status.error }} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center space-x-2 mt-6">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 rounded-lg transition-colors disabled:opacity-50"
                  style={{
                    backgroundColor: darkMode ? theme.colors.background.dark : theme.colors.background.default,
                    color: currentPage === 1 
                      ? theme.colors.text.secondary 
                      : darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary,
                    border: `1px solid ${darkMode ? theme.colors.border.dark : theme.colors.border.light}`,
                  }}
                >
                  Previous
                </button>
                
                <div className="flex items-center space-x-1">
                  {/* Show page numbers */}
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum: number;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className="w-8 h-8 rounded-lg transition-colors"
                        style={{
                          backgroundColor: currentPage === pageNum 
                            ? theme.colors.primary.main 
                            : darkMode ? theme.colors.background.dark : theme.colors.background.default,
                          color: currentPage === pageNum 
                            ? '#FFFFFF' 
                            : darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary,
                          border: currentPage === pageNum 
                            ? 'none' 
                            : `1px solid ${darkMode ? theme.colors.border.dark : theme.colors.border.light}`,
                        }}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                
                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 rounded-lg transition-colors disabled:opacity-50"
                  style={{
                    backgroundColor: darkMode ? theme.colors.background.dark : theme.colors.background.default,
                    color: currentPage === totalPages 
                      ? theme.colors.text.secondary 
                      : darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary,
                    border: `1px solid ${darkMode ? theme.colors.border.dark : theme.colors.border.light}`,
                  }}
                >
                  Next
                </button>
              </div>
            )}

            {/* Country Stats */}
            <div className="grid grid-cols-4 gap-4 mt-6">
              <div className="p-4 rounded-lg" style={{
                backgroundColor: darkMode ? theme.colors.background.dark : theme.colors.background.default,
              }}>
                <div className="flex items-center justify-between mb-2">
                  <Globe className="w-5 h-5" style={{ color: theme.colors.primary.main }} />
                  {countries.length > 0 && (
                    <span className="text-xs" style={{ color: theme.colors.text.secondary }}>
                      {((countries.length / 195) * 100).toFixed(0)}%
                    </span>
                  )}
                </div>
                <p className="text-2xl font-bold" style={{ 
                  color: darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary 
                }}>
                  {countries.length}
                </p>
                <p className="text-xs mt-1" style={{ color: theme.colors.text.secondary }}>
                  Total Countries
                </p>
              </div>
              <div className="p-4 rounded-lg" style={{
                backgroundColor: darkMode ? theme.colors.background.dark : theme.colors.background.default,
              }}>
                <div className="flex items-center justify-between mb-2">
                  <CheckCircle className="w-5 h-5" style={{ color: theme.colors.status.success }} />
                  {countries.length > 0 && (
                    <span className="text-xs" style={{ color: theme.colors.text.secondary }}>
                      {((countries.filter(c => c.is_active).length / countries.length) * 100).toFixed(0)}%
                    </span>
                  )}
                </div>
                <p className="text-2xl font-bold" style={{ 
                  color: darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary 
                }}>
                  {countries.filter(c => c.is_active).length}
                </p>
                <p className="text-xs mt-1" style={{ color: theme.colors.text.secondary }}>
                  Active Countries
                </p>
              </div>
              <div className="p-4 rounded-lg" style={{
                backgroundColor: darkMode ? theme.colors.background.dark : theme.colors.background.default,
              }}>
                <div className="flex items-center justify-between mb-2">
                  <Users className="w-5 h-5" style={{ color: theme.colors.secondary.main }} />
                  <span className="text-xs" style={{ color: theme.colors.text.secondary }}>
                    {[...new Set(countries.map(c => c.region))].length} regions
                  </span>
                </div>
                <p className="text-2xl font-bold" style={{ 
                  color: darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary 
                }}>
                  {[...new Set(countries.map(c => c.region))].length}
                </p>
                <p className="text-xs mt-1" style={{ color: theme.colors.text.secondary }}>
                  Geographic Regions
                </p>
              </div>
              <div className="p-4 rounded-lg" style={{
                backgroundColor: darkMode ? theme.colors.background.dark : theme.colors.background.default,
              }}>
                <div className="flex items-center justify-between mb-2">
                  <Flag className="w-5 h-5" style={{ color: theme.colors.status.warning }} />
                  {countries.length > 0 && (
                    <span className="text-xs" style={{ color: theme.colors.status.warning }}>
                      {((countries.filter(c => !c.is_active).length / countries.length) * 100).toFixed(0)}%
                    </span>
                  )}
                </div>
                <p className="text-2xl font-bold" style={{ 
                  color: darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary 
                }}>
                  {countries.filter(c => !c.is_active).length}
                </p>
                <p className="text-xs mt-1" style={{ color: theme.colors.text.secondary }}>
                  Inactive Countries
                </p>
              </div>
            </div>
          </div>
)}
      </div>

      {/* Country Modal */}
      {showCountryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50 p-4">
          <div className="rounded-2xl max-w-2xl w-full bg-white dark:bg-gray-800 shadow-2xl">
            <div className="p-6">
              <h2 className="text-2xl font-normal mb-6 text-gray-900 dark:text-white">
                {selectedCountry ? 'Edit Country' : 'Add New Country'}
              </h2>

              <div className="space-y-3">
              {/* ISO Code */}
              <div>
                <input
                  type="text"
                  value={countryFormData.iso_code}
                  onChange={(e) => setCountryFormData({ ...countryFormData, iso_code: e.target.value.toUpperCase() })}
                  maxLength={3}
                  disabled={!!selectedCountry}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:bg-white dark:focus:bg-gray-800 transition-all"
                  placeholder="ISO Code *"
                />
              </div>

              {/* Country Name */}
              <div className="mt-4">
                <input
                  type="text"
                  value={countryFormData.name}
                  onChange={(e) => setCountryFormData({ ...countryFormData, name: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:bg-white dark:focus:bg-gray-800 transition-all"
                  placeholder="Country Name *"
                />
              </div>

              {/* 2-Digit Code */}
              <div>
                <input
                  type="text"
                  value={countryFormData.brave_country_code}
                  onChange={(e) => setCountryFormData({ ...countryFormData, brave_country_code: e.target.value.toUpperCase() })}
                  maxLength={2}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:bg-white dark:focus:bg-gray-800 transition-all"
                  placeholder="Two Digit Country Code"
                />
              </div>

              {/* Region */}
              <div className="mt-4">
                <select
                  value={countryFormData.region}
                  onChange={(e) => setCountryFormData({ ...countryFormData, region: e.target.value })}
                  className="w-full px-4 py-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:bg-white dark:focus:bg-gray-800 transition-all appearance-none cursor-pointer"
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                    backgroundPosition: 'right 1rem center',
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: '1.5em 1.5em',
                  }}
                >
                  <option value="">Region</option>
                  <option value="North America">North America</option>
                  <option value="South America">South America</option>
                  <option value="Europe">Europe</option>
                  <option value="Africa">Africa</option>
                  <option value="Asia Pacific">Asia Pacific</option>
                  <option value="Middle East">Middle East</option>
                  <option value="Oceania">Oceania</option>
                </select>
              </div>

              {/* Currency Code */}
              <div>
                <input
                  type="text"
                  value={countryFormData.currency_code}
                  onChange={(e) => setCountryFormData({ ...countryFormData, currency_code: e.target.value.toUpperCase() })}
                  maxLength={3}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:bg-white dark:focus:bg-gray-800 transition-all"
                  placeholder="Currency Code"
                />
              </div>

              {/* Flag Emoji */}
              <div className="mt-4">
                <input
                  type="text"
                  value={countryFormData.flag_emoji}
                  onChange={(e) => setCountryFormData({ ...countryFormData, flag_emoji: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:bg-white dark:focus:bg-gray-800 transition-all"
                  placeholder="Flag Emoji"
                />
              </div>

              {/* Display Order */}
              <div className="mt-4">
                <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                  Display Order
                </label>
                <input
                  type="number"
                  value={countryFormData.display_order}
                  onChange={(e) => setCountryFormData({ ...countryFormData, display_order: parseInt(e.target.value) || 0 })}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:bg-white dark:focus:bg-gray-800 transition-all"
                  placeholder="999"
                />
              </div>

              {/* Active Status */}
              <div className="mt-4 flex items-center space-x-3">
                <button
                  type="button"
                  onClick={() => setCountryFormData({ ...countryFormData, is_active: !countryFormData.is_active })}
                  className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors ${
                    countryFormData.is_active ? 'bg-orange-400' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                      countryFormData.is_active ? 'translate-x-8' : 'translate-x-1'
                    }`}
                  />
                </button>
                <label className="text-base text-gray-700 dark:text-gray-300">
                  Active
                </label>
              </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => setShowCountryModal(false)}
                  className="px-6 py-3 rounded-lg font-medium transition-all hover:opacity-80"
                  style={{
                    backgroundColor: 'transparent',
                    color: '#FF8A65',
                    border: '2px solid #FF8A65',
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveCountry}
                  disabled={saving}
                  className="px-6 py-3 rounded-lg font-medium text-white flex items-center space-x-2 transition-all hover:opacity-90"
                  style={{
                    backgroundColor: '#FAAF90',
                    opacity: saving ? 0.5 : 1,
                  }}
                >
                  {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                  <span>{saving ? 'Saving...' : 'Save'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCountries;